import type { MouseEvent, ReactNode } from 'react';
import {
  BarcodeIcon,
  CubeIcon,
  MapPinIcon,
  ShieldWarningIcon,
  TruckIcon,
  WarehouseIcon,
  WarningIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { OrderError } from '@skupreme/api-contracts/core';
import { Badge, Button, Popover, Tooltip, cn } from '@skupreme/shared/ui';
import {
  describeProcess,
  getIssueSeverity,
  getMissingDimensionSkus,
  getOrderIssues,
  isMachineCode,
  withAddressFix,
  LABEL_GENERATION,
  type OrderIssue,
  type OrderIssueKind,
} from '../../lib/issues';
import { issueDisplayLabel } from '../../lib/issue-label';
import { StopRowClick } from './StopRowClick';

type OrderIssuesProps = {
  errors: OrderError[] | undefined;
  isShipped?: boolean;
  /** Given only on the tabs that offer address validation. */
  addressFix?: (event: MouseEvent) => void;
  /** Reasons the screen works out itself, absent from `order_error`. */
  extra?: OrderIssue[];
  /** `aside` stacks the chips in a column of one width. */
  layout?: 'inline' | 'aside';
  className?: string;
};

const KIND_ICONS: Record<OrderIssueKind, Icon> = {
  address: MapPinIcon,
  inventory: CubeIcon,
  provider: WarehouseIcon,
  label: BarcodeIcon,
  shipment: TruckIcon,
  risk: ShieldWarningIcon,
  unknown: WarningIcon,
};

/* Past this a reason is prose the pipeline wrote, not a label it stamped. */
const SENTENCE_LENGTH = 40;

function IssueChip({
  issue,
  isBlock,
}: {
  issue: OrderIssue;
  isBlock?: boolean;
}) {
  const kind = issue.kind || 'unknown';
  const KindIcon = KIND_ICONS[kind];
  const isBlocking = getIssueSeverity(kind) === 'blocking';
  const hasDistinctDetail =
    !!issue.detail && issue.detail.trim() !== issue.reason.trim();
  /* A short reason with no detail explains nothing, so point at support. */
  const needsHelp =
    !issue.label &&
    !hasDistinctDetail &&
    (kind === 'unknown' || issue.reason.length < SENTENCE_LENGTH);
  const step = describeProcess(issue.process);

  const chip = (
    <Badge
      tone={isBlocking ? 'danger' : 'warning'}
      className={`sk:min-w-0 sk:text-xs sk:font-normal ${isBlock ? 'sk:w-full' : 'sk:max-w-60'}`}
    >
      <KindIcon size={12} weight="bold" className="sk:shrink-0" />
      {/* `min-w-0`: without it the flex child grows past the column instead of clipping. */}
      <span className="sk:min-w-0 sk:truncate">
        {issue.label || issueDisplayLabel(issue.reason)}
      </span>
    </Badge>
  );

  const trigger: ReactNode = issue.onFix ? (
    <button
      type="button"
      onClick={issue.onFix}
      className={`sk:cursor-pointer sk:border-0 sk:bg-transparent sk:p-0 sk:outline-none ${isBlock ? 'sk:w-full' : ''}`}
    >
      {chip}
    </button>
  ) : (
    <span
      className={`sk:inline-flex sk:min-w-0 sk:cursor-default ${isBlock ? 'sk:w-full' : ''}`}
    >
      {chip}
    </span>
  );

  return (
    <Tooltip
      placement="bottom start"
      content={
        <span className="sk:flex sk:flex-col sk:gap-1">
          <span className="sk:text-fg-tertiary">Attention needed</span>
          <span>{issue.label || issueDisplayLabel(issue.reason)}</span>
          {/* A V2 code only repeats the label in other words; prose the pipeline wrote can add to it. */}
          {issue.label && !isMachineCode(issue.reason) ? (
            <span className="sk:text-fg-tertiary">
              {issueDisplayLabel(issue.reason)}
            </span>
          ) : null}
          {issue.hint ? (
            <span className="sk:text-fg-tertiary">{issue.hint}</span>
          ) : null}
          {/* A label issue's detail is the link itself, not an explanation. */}
          {hasDistinctDetail &&
          (!issue.label ||
            issue.reason === 'connections_rejected' ||
            issue.process === 'provider_refused') ? (
            <span className="sk:text-fg-tertiary">{issue.detail}</span>
          ) : null}
          {issue.onFix ? (
            <span className="sk:text-fg-tertiary">
              {issue.process === LABEL_GENERATION
                ? 'Click to open the label form'
                : 'Click to fix'}
            </span>
          ) : null}
          {needsHelp ? (
            <span className="sk:text-fg-tertiary">
              {step ? `Reported by ${step.toLowerCase()}. ` : null}
              Please contact support and we will look into it.
            </span>
          ) : null}
        </span>
      }
    >
      {trigger}
    </Tooltip>
  );
}

/**
 * EN-4544: the rest of one severity's issues, behind "+N" beside its first: the list keeps one chip per severity
 * and the row its height. Each chip's tooltip says what to do, as in the row.
 */
function MoreIssues({ issues }: { issues: OrderIssue[] }) {
  return (
    <Popover
      aria-label={`${issues.length} more`}
      placement="bottom start"
      trigger={
        <Button variant="pill" size="sm" className="sk:shrink-0">
          +{issues.length}
        </Button>
      }
    >
      <div className="sk:flex sk:w-80 sk:flex-col sk:gap-1.5">
        {issues.map(issue => (
          <IssueChip key={issue.key} issue={issue} isBlock />
        ))}
      </div>
    </Popover>
  );
}

/* The label step's reason is API copy; say what it means and where to fix it. */
function withLabelBlock(issues: OrderIssue[]): OrderIssue[] {
  return issues.map(issue => {
    if (issue.process !== LABEL_GENERATION) {
      return issue;
    }

    const { skus } = getMissingDimensionSkus(issue.reason);
    const link = issue.detail;

    return {
      ...issue,
      label: skus.length
        ? 'Missing package dimensions'
        : 'Label needs manual packaging',
      /* The row is itself a link and links cannot nest, so this opens the tab. */
      onFix: link
        ? (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            window.open(link, '_blank', 'noopener,noreferrer');
          }
        : undefined,
    };
  });
}

export function OrderIssues({
  errors,
  isShipped,
  addressFix,
  extra,
  layout = 'inline',
  className,
}: OrderIssuesProps) {
  const recorded = withLabelBlock([
    ...getOrderIssues(errors, isShipped),
    ...(extra || []),
  ]);
  const listed = addressFix ? withAddressFix(recorded, addressFix) : recorded;

  /* What stops the order comes before what it is merely waiting on. */
  const issues = [
    ...listed.filter(issue => getIssueSeverity(issue.kind) === 'blocking'),
    ...listed.filter(issue => getIssueSeverity(issue.kind) === 'warning'),
  ];

  if (issues.length === 0) {
    return null;
  }

  /* Beside the order ID: one chip per severity, the rest behind "+N". */
  if (layout === 'aside') {
    const groups = [
      issues.filter(issue => getIssueSeverity(issue.kind) === 'blocking'),
      issues.filter(issue => getIssueSeverity(issue.kind) === 'warning'),
    ].filter(group => group.length > 0);

    return (
      <div
        className={cn(
          'sk:flex sk:max-w-50 sk:min-w-0 sk:flex-col sk:items-stretch sk:gap-1.5',
          className,
        )}
      >
        {groups.map(([first, ...rest]) => (
          <span
            key={first.key}
            className="sk:flex sk:w-full sk:min-w-0 sk:items-center sk:gap-1"
          >
            <StopRowClick className="sk:min-w-0 sk:flex-1">
              <IssueChip issue={first} isBlock />
            </StopRowClick>
            {rest.length ? (
              <StopRowClick>
                <MoreIssues issues={rest} />
              </StopRowClick>
            ) : null}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'sk:mt-2 sk:flex sk:flex-wrap sk:items-center sk:gap-2',
        className,
      )}
    >
      {issues.map(issue => (
        <StopRowClick key={issue.key}>
          <IssueChip issue={issue} />
        </StopRowClick>
      ))}
    </div>
  );
}
